import type { CSSProperties, ReactNode } from 'react';
import {
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_SHORT_LABELS,
  MAIL_STATUS_LABELS,
  type Invoice,
  type InvoiceStatus,
  type MailStatus,
} from '@faktorplus/shared';
import {
  BanknoteIcon,
  ClockOverdueIcon,
  CoinsIcon,
  FileMissingIcon,
  PencilIcon,
  UndoIcon,
  UserOffIcon,
} from './icons';
import styles from './ui.module.css';

/** Status colour is semantic, not decorative — it maps to who is blocked. */
const STATUS_TONE: Record<InvoiceStatus, string> = {
  NEW: styles.pillNeutral!,
  ASSIGNED: styles.pillWaiting!,
  DISPUTED: styles.pillCritical!,
  APPROVED_BY_MANAGER: styles.pillAccent!,
  APPROVED_FOR_PAYMENT: styles.pillAccent!,
  PAID: styles.pillOk!,
};

/**
 * How far along the flow each status sits, as a 0–1 fill for the ClickUp-style
 * ring: NEW is an empty outline, PAID a full circle. DISPUTED is the branch
 * back toward the start, so it reads as early rather than advanced.
 */
const STATUS_PROGRESS: Record<InvoiceStatus, number> = {
  NEW: 0,
  ASSIGNED: 0.25,
  DISPUTED: 0.25,
  APPROVED_BY_MANAGER: 0.5,
  APPROVED_FOR_PAYMENT: 0.75,
  PAID: 1,
};

function fillStyle(fraction: number): CSSProperties {
  return { ['--status-fill' as string]: String(fraction) } as CSSProperties;
}

export function StatusPill({ status, short = false }: { status: InvoiceStatus; short?: boolean }) {
  const labels = short ? INVOICE_STATUS_SHORT_LABELS : INVOICE_STATUS_LABELS;

  return (
    <span className={`${styles.pill} ${STATUS_TONE[status]}`} style={fillStyle(STATUS_PROGRESS[status])}>
      {labels[status]}
    </span>
  );
}

/**
 * Status plus whatever is blocking the invoice.
 *
 * The flags are rendered *beside* the status, never instead of it: a paid
 * subcontractor invoice and a disputed one both lack a protocol, and replacing
 * the status with "Brak protokołu" made them indistinguishable in the register.
 *
 * Fixed order, most urgent first: overdue (money), cash (outside the bank
 * flow), missing protocol (paperwork), released without the owner (who decided).
 */
export function InvoiceStateBadge({ invoice, short = false }: { invoice: Invoice; short?: boolean }) {
  // A correction sits outside the flow, so a workflow status would be a lie —
  // its two facts are "what it corrects" and "is the delta accounted for".
  if (invoice.correctsInvoiceId) {
    return (
      <span className={styles.badgeGroup}>
        <Flag tone="warning" icon={<PencilIcon />}>
          Korekta
          {!short && invoice.correctsNumber ? ` → ${invoice.correctsNumber}` : ''}
        </Flag>
        {invoice.correctionSettledAt === null && <Flag icon={<UndoIcon />}>Nierozliczona</Flag>}
      </span>
    );
  }

  return (
    <span className={styles.badgeGroup}>
      <StatusPill status={invoice.status} short={short} />
      <InvoiceFlags invoice={invoice} />
    </span>
  );
}

/** The flags alone — for tables that already have a status column, or none. */
export function InvoiceFlags({ invoice }: { invoice: Invoice }) {
  const needsProtocol = invoice.requiresHandoverProtocol && !invoice.documentsCompleteAt;

  return (
    <>
      {isOverdue(invoice) && <OverdueFlag days={Math.abs(invoice.daysUntilDue ?? 0)} />}
      {/* Says why this row is missing from payables, on the one screen that
          still shows it. */}
      {invoice.paymentMethod === 'CASH' && <CashFlag />}
      {isPartiallyPaid(invoice) && <PartiallyPaidFlag />}
      {needsProtocol && <MissingProtocolFlag />}
      {awaitsOwner(invoice) && <WithoutOwnerFlag />}
    </>
  );
}

export function hasInvoiceFlags(invoice: Invoice): boolean {
  return (
    isOverdue(invoice) ||
    invoice.paymentMethod === 'CASH' ||
    isPartiallyPaid(invoice) ||
    (invoice.requiresHandoverProtocol && !invoice.documentsCompleteAt) ||
    awaitsOwner(invoice)
  );
}

/**
 * Some transfers recorded, the rest still owed (24.09.2026). Derived, never a
 * status of its own: the flow position and the money are two different facts.
 */
function isPartiallyPaid(invoice: Invoice): boolean {
  return invoice.paidAt === null && invoice.paidAmount > 0;
}

/**
 * Released without the owner and still not signed off. Once the owner approves
 * after the fact (24.09.2026), the flag disappears from every list — the
 * permanent fact stays on the invoice's detail view, not on its badge.
 */
function awaitsOwner(invoice: Invoice): boolean {
  return invoice.approvedForPaymentWithoutOwner && invoice.ownerApprovedAt === null;
}

function isOverdue(invoice: Invoice): boolean {
  return invoice.isOverdue && invoice.daysUntilDue !== null;
}

export type InvoiceRowTone = 'late' | 'cash' | null;

/**
 * Which tint a table row deserves. Overdue wins over cash: an unpaid bill past
 * its date is the owner's problem today, the payment method is not.
 */
export function invoiceRowTone(invoice: Invoice): InvoiceRowTone {
  if (isOverdue(invoice)) return 'late';
  if (invoice.paymentMethod === 'CASH') return 'cash';
  return null;
}

/** Colours match the invoice pills: amber is waiting, red is somebody's problem. */
const MAIL_STATUS_TONE: Record<MailStatus, string> = {
  PENDING: styles.pillWaiting!,
  SENT: styles.pillOk!,
  FAILED: styles.pillCritical!,
};

const MAIL_PROGRESS: Record<MailStatus, number> = {
  PENDING: 0.4,
  SENT: 1,
  FAILED: 0.4,
};

/** Whether a message the app owes somebody has actually left. */
export function MailStatusPill({ status }: { status: MailStatus }) {
  return (
    <span className={`${styles.pill} ${MAIL_STATUS_TONE[status]}`} style={fillStyle(MAIL_PROGRESS[status])}>
      {MAIL_STATUS_LABELS[status]}
    </span>
  );
}

/** Modifier pill: same shape as a status pill, an icon instead of the ring. */
export function Flag({
  children,
  icon,
  tone = 'critical',
}: {
  children: ReactNode;
  icon?: ReactNode;
  tone?: 'critical' | 'cash' | 'warning';
}) {
  const toneClass =
    tone === 'cash' ? styles.pillCash : tone === 'warning' ? styles.pillWaiting : styles.pillCritical;
  return (
    <span className={`${styles.pill} ${styles.pillFlat} ${toneClass}`}>
      {icon && (
        <span className={styles.pillIcon} aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
    </span>
  );
}

export function CashFlag() {
  return (
    <Flag tone="cash" icon={<BanknoteIcon />}>
      Gotówka
    </Flag>
  );
}

export function MissingProtocolFlag() {
  return <Flag icon={<FileMissingIcon />}>Brak protokołu</Flag>;
}

/** Part of the invoice is already covered by recorded transfers. */
export function PartiallyPaidFlag() {
  return (
    <Flag tone="warning" icon={<CoinsIcon />}>
      Częściowo zapłacona
    </Flag>
  );
}

/** An accountant released this for payment in the owner's place. Permanent. */
export function WithoutOwnerFlag() {
  return (
    <Flag tone="warning" icon={<UserOffIcon />}>
      Bez akceptacji właściciela
    </Flag>
  );
}

export function OverdueFlag({ days }: { days: number }) {
  return <Flag icon={<ClockOverdueIcon />}>Po terminie · {days} dni</Flag>;
}
